<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { components as StrapiComponents } from '@core/types/clients/strapi'
import { StyleFormatter } from '@nimbus/utils/styleFormatter'
import type { StrapiBlock } from '#imports'

interface MediaQueryStyles {
    fontSize?: string
    fontWeight?: string
    textAlign?: 'left' | 'center' | 'right'
}

const props = defineProps<{
    textBlock: StrapiComponents['schemas']['DynamicTextBlockComponent']
}>()

// Extract formatted values using the StyleFormatter utility
const titleFontSize = computed(() => StyleFormatter.extractValue(props.textBlock.titleFontSize))
const fontSize = computed(() => StyleFormatter.extractValue(props.textBlock.fontSize))
const titleFontWeight = computed(() => StyleFormatter.extractValue(props.textBlock.titleFontWeight))
const fontWeight = computed(() => StyleFormatter.extractValue(props.textBlock.fontWeight))

// Process media queries
const processedMediaQueries = computed(() =>
    StyleFormatter.processMediaQueries(props.textBlock.mediaQuery)
)

// Initialize with default values to avoid hydration mismatch
const mediaQueryStyles = ref<MediaQueryStyles>({
    fontSize: fontSize.value,
    fontWeight: fontWeight.value,
    textAlign: props.textBlock.alignment
})

const mediaQueryTitleStyles = ref<MediaQueryStyles>({
    fontSize: titleFontSize.value,
    fontWeight: titleFontWeight.value,
    textAlign: props.textBlock.titleAlignment
})

const checkMediaQueries = () => {
    if (!props.textBlock.mediaQuery) return

    // Reset to default values first
    mediaQueryStyles.value = {
        fontSize: fontSize.value,
        fontWeight: fontWeight.value,
        textAlign: props.textBlock.alignment
    }

    mediaQueryTitleStyles.value = {
        fontSize: titleFontSize.value,
        fontWeight: titleFontWeight.value,
        textAlign: props.textBlock.titleAlignment
    }

    // Skip media query processing during SSR
    if (typeof window === 'undefined') return

    for (const mq of processedMediaQueries.value) {
        if (!mq) continue
        const mediaQuery = window.matchMedia(`(min-width: ${mq.media})`)
        if (mediaQuery.matches) {
            if (mq.fontSize) mediaQueryStyles.value.fontSize = mq.fontSize
            if (mq.fontWeight) mediaQueryStyles.value.fontWeight = mq.fontWeight
            if (mq.alignment) mediaQueryStyles.value.textAlign = mq.alignment
            if (mq.titleFontSize) mediaQueryTitleStyles.value.fontSize = mq.titleFontSize
            if (mq.titleFontWeight) mediaQueryTitleStyles.value.fontWeight = mq.titleFontWeight
            if (mq.titleAlignment) mediaQueryTitleStyles.value.textAlign = mq.titleAlignment
        }
    }
}

onMounted(() => {
    checkMediaQueries()
    window.addEventListener('resize', checkMediaQueries)
})

onUnmounted(() => {
    window.removeEventListener('resize', checkMediaQueries)
})
</script>

<template>
    <div v-if="props.textBlock.title" class="text text__title" :style="mediaQueryTitleStyles">
        {{ props.textBlock.title }}
    </div>
    <div class="text" :class="`text--${props.textBlock.alignment}`" :style="mediaQueryStyles">
        <LazyNimbusStrapiBlockRenderer :content="props.textBlock.richtext as Array<StrapiBlock> ?? []" />
    </div>
</template>

<style scoped lang="scss">
.text {
    word-wrap: break-word;
    margin: 0 auto;

    &__title {
        margin-bottom: 16px;
    }

    &--left {
        text-align: left;
    }

    &--center {
        text-align: center;
    }

    &--right {
        text-align: right;
    }

    :deep(a) {
        color: inherit;
        text-decoration: underline;
    }
}
</style>
